import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import type Quill from 'quill/core';
import type { Delta } from 'quill/core';
import scrollIntoView from 'scroll-into-view-if-needed';
import { motion } from 'framer-motion';
import { useScroll } from '@use-gesture/react';
import styles from './VersionChanges.module.scss';
import IconButton from '../../components/IconButton';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import arrowLeftIcon from '../../icons/non-prefilled/16x16/arrowLeft.svg';
import VersionChangeFormat from '../../editor/formats/VersionChange';
import { useQuill } from '../../editor/QuillContext';
import HistoricalPost from './HistoricalPost';
import PageMeta from '../../utils/PageMeta';
import { getScrollContainer } from '../listenToPostScroll';
import postPath from '../../utils/url/postPath';
import usePostChromeVisible from '../usePostChromeVisible';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import useHistoryRange from './useHistoryRange';
import VersionChangesWall from './VersionChangesWall';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

export interface VersionChange {
  id: string;
  version: number;
  authorId: string;
}

type VersionChangesContextValue = {
  postId: string;
  versionFrom: number;
  versionTo: number;
  content: Delta | undefined;
  allVersionChanges: VersionChange[] | undefined;
  isInitialized: boolean;
  updateVersionChanges: (quill: Quill) => void;
  versionChanges: VersionChange[];
  focusedIndex: number;
  focusedId: string | null;
  focusById: (id: string) => void;
  goToNextChange: () => void;
  goToPreviousChange: () => void;
};

const VersionChangesContext =
  React.createContext<VersionChangesContextValue | null>(null);

export const useVersionChangesContext = () => {
  const value = useContext(VersionChangesContext);
  if (!value) {
    throw new Error(
      'useVersionChangesContext must be used within a VersionChangesContext.Provider',
    );
  }
  return value;
};

const VERSION_CHANGES_WRAPPER_POST_FRAGMENT = gql(`
  fragment VersionChangesWrapper_post on Post {
    id
    title
  }
`);

interface VersionChangesWrapperProps {
  post?: DocumentType<typeof VERSION_CHANGES_WRAPPER_POST_FRAGMENT> | null;
  topbar?: React.ReactNode;
  children?: React.ReactNode;
  initialTitle: string;
}

export const VersionChangesWrapper = ({
  post,
  topbar,
  children,
  initialTitle,
}: VersionChangesWrapperProps) => {
  return (
    <div className={styles.wrapperContainer}>
      {topbar}
      <div
        className={classNames(
          styles.wrapperContent,
          'no-padding-x',
          'no-padding-y',
        )}
        id="content"
      >
        <PageMeta
          title={post ? `${post.title} - ${initialTitle}` : initialTitle}
        />
        {children}
      </div>
    </div>
  );
};

interface VersionChangesTopBarProps {
  versionMeta?: React.ReactNode;
}

export const VersionChangesTopBar = ({
  versionMeta,
}: VersionChangesTopBarProps) => {
  const {
    postId,
    isInitialized,
    versionChanges,
    focusedIndex,
    goToNextChange,
    goToPreviousChange,
  } = useVersionChangesContext();

  const isMobile = useIsSize(Sizes.SM);
  const isChromeVisible = usePostChromeVisible({ enabled: isMobile });

  return (
    <div
      className={classNames(styles.barContainer, {
        [styles.hide]: !isChromeVisible,
      })}
    >
      <div className={styles.bar}>
        <div className={styles.content}>
          <IconButton
            applyNegativeMargin
            to={postPath({ id: postId, title: '' })}
            aria-label="Go back to post"
          >
            {arrowLeftIcon}
          </IconButton>
          <div className={styles.versionMeta}>{versionMeta}</div>
        </div>
        <div className={styles.cyclingContainer}>
          {isInitialized && (
            <div className={styles.cyclingLabel}>
              {versionChanges.length > 0 ? (
                <>
                  {focusedIndex + 1} / {versionChanges.length} changes
                </>
              ) : (
                'No changes'
              )}
            </div>
          )}

          <div className={styles.cyclingButtons}>
            <Button
              aria-label="Previous Change"
              disabled={focusedIndex <= 0}
              className={styles.cyclingButton}
              onClick={goToPreviousChange}
            >
              <Caret block size="regular" direction="up" />
            </Button>
            <Button
              aria-label="Next Change"
              disabled={focusedIndex >= versionChanges.length - 1}
              className={styles.cyclingButton}
              onClick={goToNextChange}
            >
              <Caret block size="regular" direction="down" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const VersionChangesContent = () => {
  const {
    content,
    updateVersionChanges,
    postId,
    versionFrom,
    versionTo,
    focusById,
    focusedId,
    versionChanges,
  } = useVersionChangesContext();

  return (
    <div className={styles.contentContainer}>
      <div className={styles.content}>
        {content && (
          <HistoricalPost
            content={content}
            onBlotComponentsInitialLoad={updateVersionChanges}
          />
        )}
      </div>
      <div className={styles.changes}>
        <VersionChangesWall
          postId={postId}
          versionFrom={versionFrom}
          versionTo={versionTo}
          onFocus={focusById}
          versionChanges={versionChanges}
          focusedId={focusedId}
        />
      </div>
    </div>
  );
};

const moveFocusedClass = (root: HTMLElement, id: string | null) => {
  const focusedElements = new Set<Element>();
  if (id) {
    root
      .querySelectorAll(`[${VersionChangeFormat.keyName}="${id}"]`)
      .forEach((element) => {
        focusedElements.add(element);
        element.classList.add('focused-diff');
      });
  }

  Array.from(root.getElementsByClassName('focused-diff')).forEach((element) => {
    if (!focusedElements.has(element)) {
      element.classList.remove('focused-diff');
    }
  });
};

const scrollVersionChangeIntoView = (root: HTMLElement, id: string) => {
  const element = root.querySelector<HTMLElement>(
    `[${VersionChangeFormat.keyName}="${id}"]`,
  );
  if (element) {
    scrollIntoView(element, { scrollMode: 'if-needed' });
  }
};

interface VersionChangeState {
  isInitialized: boolean;
  versionChanges: VersionChange[];
  focusedId: string | null;
}

type Action =
  | { type: 'focus'; id: string | null }
  | { type: 'setVersionChanges'; versionChanges: VersionChange[] };

const reducer = (
  state: VersionChangeState,
  action: Action,
): VersionChangeState => {
  switch (action.type) {
    case 'focus':
      return { ...state, focusedId: action.id };
    case 'setVersionChanges':
      return {
        ...state,
        isInitialized: true,
        versionChanges: action.versionChanges,
        focusedId: action.versionChanges.find((v) => v.id === state.focusedId)
          ? state.focusedId
          : (action.versionChanges[0]?.id ?? null),
      };
    default:
      return state;
  }
};

const initialState: VersionChangeState = {
  isInitialized: false,
  versionChanges: [],
  focusedId: null,
};

interface VersionChangesProps {
  children: React.ReactNode;
  postId: string;
  versionFrom: number;
  versionTo: number;
}

export const VersionChanges = ({
  children,
  postId,
  versionFrom,
  versionTo,
}: VersionChangesProps) => {
  const { content, allVersionChanges } = useHistoryRange({
    postId,
    versionFrom,
    versionTo,
  });

  const { quill } = useQuill();

  const [{ isInitialized, versionChanges, focusedId }, reducerDispatch] =
    useReducer(reducer, initialState);

  const updateVersionChanges = useCallback(
    ({ root }: Quill) => {
      if (!allVersionChanges) return;

      const elements = Array.from(
        root.querySelectorAll<HTMLElement>(`[${VersionChangeFormat.keyName}]`),
      );

      const availableIds = elements.map((element) =>
        element.getAttribute(VersionChangeFormat.keyName),
      );

      const newVersionChanges = allVersionChanges.filter((v) =>
        availableIds.includes(v.id),
      );

      reducerDispatch({
        type: 'setVersionChanges',
        versionChanges: newVersionChanges,
      });
    },
    [allVersionChanges],
  );

  const focusedIndex = Math.max(
    versionChanges.findIndex((v) => v.id === focusedId),
    0,
  );

  useEffect(() => {
    if (!quill) return undefined;

    const listener = (event: MouseEvent) => {
      if (event.target instanceof Node) {
        const element =
          event.target instanceof Element
            ? event.target
            : event.target.parentElement;
        const id = element
          ?.closest(`[${VersionChangeFormat.keyName}]`)
          ?.getAttribute(VersionChangeFormat.keyName);
        if (id) {
          reducerDispatch({ type: 'focus', id });
        }
      }
    };
    quill.root.addEventListener('click', listener);
    return () => {
      quill.root.removeEventListener('click', listener);
    };
  }, [quill, versionChanges, reducerDispatch]);

  useEffect(() => {
    if (quill) {
      moveFocusedClass(quill.root, focusedId);
      if (focusedId) {
        scrollVersionChangeIntoView(quill.root, focusedId);
      }
    }
  }, [quill, focusedId]);

  const value = useMemo<VersionChangesContextValue>(
    () => ({
      content,
      allVersionChanges,
      isInitialized,
      focusById(id: string) {
        reducerDispatch({ type: 'focus', id });
      },
      goToNextChange() {
        const newId = versionChanges[focusedIndex + 1]?.id ?? null;
        reducerDispatch({ type: 'focus', id: newId });
        if (quill && newId) {
          scrollVersionChangeIntoView(quill.root, newId);
        }
      },
      goToPreviousChange() {
        const newId = versionChanges[focusedIndex - 1]?.id ?? null;
        reducerDispatch({ type: 'focus', id: newId });
        if (quill && newId) {
          scrollVersionChangeIntoView(quill.root, newId);
        }
      },
      updateVersionChanges,
      versionChanges,
      focusedIndex,
      focusedId,
      postId,
      versionFrom,
      versionTo,
    }),
    [
      content,
      focusedIndex,
      focusedId,
      versionChanges,
      allVersionChanges,
      quill,
      updateVersionChanges,
      isInitialized,
      postId,
      versionFrom,
      versionTo,
    ],
  );

  return (
    <VersionChangesContext.Provider value={value}>
      {children}
    </VersionChangesContext.Provider>
  );
};

const FOOTER_ANIMATION_TRANSITION = {
  duration: 0.2,
};

const STICKY_FOOTER_THRESHOLD = 500;

interface VersionChangesFooterProps {
  children: React.ReactNode;
  header: React.ReactNode;
}

export const VersionChangesFooter = ({
  children,
  header,
}: VersionChangesFooterProps) => {
  const { allVersionChanges } = useVersionChangesContext();
  const [isHovered, setIsHovered] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const isChromeVisible = usePostChromeVisible({ enabled: isSticky });
  const waypointRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTargetRef = useRef<EventTarget | null>(null);

  useLayoutEffect(() => {
    const scrollContainer = getScrollContainer();
    if (!scrollContainer || isEnabled) return undefined;

    scrollTargetRef.current =
      scrollContainer === document.documentElement ? window : scrollContainer;
    const footerHeight = footerRef.current?.clientHeight ?? 0;

    const observer = new ResizeObserver(() => {
      const { clientHeight, scrollHeight } = scrollContainer;

      if (
        scrollHeight - footerHeight >
        clientHeight + STICKY_FOOTER_THRESHOLD
      ) {
        setIsEnabled(true);
        setIsSticky(true);
      }
    });

    observer.observe(scrollContainer);

    return () => {
      observer.disconnect();
    };
  }, [isEnabled, allVersionChanges]);

  useScroll(
    ({ values: [, scrollTop], target }) => {
      const clientHeight =
        target instanceof HTMLElement
          ? target.clientHeight
          : window.innerHeight;

      const waypointTop = waypointRef.current?.offsetTop ?? 0;
      const scrollBottom = scrollTop + clientHeight;
      setIsSticky(scrollBottom < waypointTop);
    },
    { target: scrollTargetRef, enabled: isEnabled },
  );

  const isExpanded = !isSticky || isHovered || isChromeVisible;

  return (
    <>
      <div className={styles.footerWaypoint} ref={waypointRef} />
      <motion.div
        animate={{
          bottom: isExpanded ? 0 : -(contentRef.current?.clientHeight ?? 0),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={classNames(styles.footerContainer, {
          [styles.isSticky]: isSticky,
        })}
        transition={FOOTER_ANIMATION_TRANSITION}
      >
        <motion.div
          initial={false}
          animate={{
            paddingTop: isExpanded ? 26 : 16,
            paddingBottom: isExpanded ? 26 : 0,
          }}
          className={styles.footer}
          ref={footerRef}
          transition={FOOTER_ANIMATION_TRANSITION}
        >
          <motion.div
            initial={false}
            style={{ paddingBottom: isExpanded ? 20 : 16 }}
            transition={FOOTER_ANIMATION_TRANSITION}
          >
            {header}
          </motion.div>
          <div ref={contentRef}>{children}</div>
        </motion.div>
      </motion.div>
    </>
  );
};
